import {computed} from '@angular/core';
import {patchState, signalStoreFeature, type, withComputed, withMethods} from '@ngrx/signals';
import {Question} from '../../models/game-data.model';
import {GameState} from '../../models/game-models.model';
import {CurrentQuestion, isQuestionCompleted, MAX_ATTEMPTS_PER_QUESTION, startQuestionState} from '../game-state.model';
import {
  addGuess,
  goToQuestion,
  markCorrect,
  markHintAsked,
  pushFinishedState,
  registerWrongGuess,
  toggleAnswerOnMap,
} from '../game.operators';
import {Answer} from '../../models/answer.model';
import {GameFeatureInput} from './feature-input';
import {computeScoreFromStates} from './with-score.feature';

/** Removes and returns a uniformly random element from `items` (mutates `items`). */
function takeRandom<T>(items: T[]): T {
  return items.splice(Math.floor(Math.random() * items.length), 1)[0]; //NOSONAR
}

/** In-place Fisher–Yates shuffle. */
function shuffle<T>(items: T[]): void {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1)); //NOSONAR
    [items[i], items[j]] = [items[j], items[i]];
  }
}

/**
 * Contributes the core question flow: starting/stopping a game, advancing
 * questions and registering guesses/hints.
 */
export function withQuestionFlow() {
  return signalStoreFeature(
    type<GameFeatureInput>(),
    withComputed((store) => ({
      /**
       * The question currently being played, derived from `currentGameQuestions`,
       * `currentQuestionIndex`, and `phase`. Returns `null` when not playing or
       * when the index is out of range.
       */
      currentQuestion: computed<CurrentQuestion | null>(() => {
        if (store.phase() !== 'playing') return null;
        const q = store.currentGameQuestions()[store.currentQuestionIndex()];
        return q ? {...q, index: store.currentQuestionIndex()} : null;
      }),
      currentQuestionCompleted: computed(() => {
        const completed = isQuestionCompleted(store.questionState());
        return completed;
      }),
      isShowingAnswerOnMap: computed(() => store.showAnswerOnMap()),
      showHint: computed(() => {
        const questionState = store.questionState();
        return (
          questionState.attempt > 1 &&
          questionState.attempt <= MAX_ATTEMPTS_PER_QUESTION &&
          questionState.feedback === 'wrong'
        );
      }),
    })),
    withMethods((store) => {
      /** Key for a theme's seen-set; the no-theme ("All themes") game uses `'all'`. */
      const poolKey = (themeId: number | null): string => (themeId == null ? 'all' : String(themeId));

      /**
       * Stable identity for a question, resilient to `localStorage` round-trips (which
       * discard object identity). A question is uniquely identified by its answer layer
       * and the correct feature value within that layer.
       */
      const questionKey = (question: Question): string =>
        `${question.gameSetting.gameLayer}|${question.gameSetting.featureIdCorrectValue}`;

      /**
       * Draws `randomQuestionsPerGame` questions for the given theme (or the whole set when
       * `themeId` is `null`). The drawable pool is implicit: it is the theme's questions
       * minus those already seen.
       *
       * Every not-yet-seen question is drawn before any question repeats: unseen questions
       * are taken first, and only if the theme runs dry mid-game is the seen-set recycled to
       * top up the remainder. The resulting questions are then shuffled so this ordering is
       * not visible to the player.
       */
      const drawQuestions = (themeId: number | null): Question[] => {
        const key = poolKey(themeId);
        const source = themeId == null
          ? store.allQuestions()
          : store.allQuestions().filter((question) => question.themeId === themeId);

        let seenKeys = [...(store.previousGamesQuestions()[key] ?? [])];
        const seenKeySet = new Set(seenKeys);
        const unseen = source.filter((question) => !seenKeySet.has(questionKey(question)));

        const count = Math.min(store.randomQuestionsPerGame(), source.length);
        const selected: Question[] = [];

        // 1. Exhaust the unseen questions first — they are guaranteed to appear.
        while (selected.length < count && unseen.length > 0) {
          selected.push(takeRandom(unseen));
        }

        // 2. Theme ran dry mid-game: recycle the seen-set and top up from the rest.
        if (selected.length < count) {
          seenKeys = [];
          const selectedKeys = new Set(selected.map(questionKey));
          const recycled = source.filter((question) => !selectedKeys.has(questionKey(question)));
          while (selected.length < count) {
            selected.push(takeRandom(recycled));
          }
        }

        shuffle(selected);

        patchState(store, {
          previousGamesQuestions: {
            ...store.previousGamesQuestions(),
            [key]: [...seenKeys, ...selected.map(questionKey)],
          },
        });
        return selected;
      };

      const startGame = (themeId: number | null): void => {
        const currentGameQuestions = drawQuestions(themeId);

        patchState(store, {
          phase: 'playing',
          score: 0,
          themeId,
          currentGameQuestions,
          finishedQuestionStates: [],
          currentQuestionIndex: 0,
          questionState: {...startQuestionState},
          showAnswerOnMap: false,
          persistedGuesses: [],
        });
      };

      return {
        drawQuestions,
        startGame,

        /** Sets the game phase. */
        setPhase(phase: GameState): void {
          patchState(store, {phase});
        },

        /**
         * Stops the current game, persisting the current question state and
         * returning to the starting phase.
         */
        stopGame(): void {
          patchState(store, pushFinishedState());
          patchState(store, {phase: 'starting'});
        },

        /**
         * Advances to the next question, persisting the finished one.
         * When on the last question, transitions to the game-over phase and
         * computes the final score instead of advancing.
         */
        nextQuestion(): void {
          patchState(store, pushFinishedState());
          if (store.currentQuestionIndex() === store.currentGameQuestions().length - 1) {
            patchState(store, {
              phase: 'gameOver',
              score: computeScoreFromStates(
                store.finishedQuestionStates(),
                store.possibleScores(),
                store.hintReduction()
              ),
            });
          } else {
            patchState(store, goToQuestion(store.currentQuestionIndex() + 1));
          }
        },

        /** Marks the current question as correctly answered. */
        onCorrectGuess(): void {
          patchState(store, markCorrect());
        },

        /** Registers a wrong guess (increments attempt or ends the question). */
        onWrongGuess(): void {
          patchState(store, registerWrongGuess(MAX_ATTEMPTS_PER_QUESTION));
        },

        /** Toggles whether the answer layer is shown on the map. */
        toggleShowAnswerOnMap(): void {
          patchState(store, toggleAnswerOnMap());
        },

        /** Records that the player asked for a hint. */
        askedHint(): void {
          patchState(store, markHintAsked());
        },

        /**
         * Registers a guess from the map. Ignores duplicate guesses for the same
         * feature. Immediately routes to {@link markCorrect} or
         * {@link registerWrongGuess} so the map component only handles styling.
         */
        onGuess(guess: Answer): void {
          if (store.guesses().some(g => g.feature === guess.feature)) return;
          patchState(store, addGuess(guess));
          if (!isQuestionCompleted(store.questionState())) {
            patchState(store, guess.isCorrect
              ? markCorrect()
              : registerWrongGuess(MAX_ATTEMPTS_PER_QUESTION));
          }
        },
      };
    })
  );
}
